/**
 * Abandoned Cart Recovery System
 * Premium DTC Exit Intent & Inactivity Logic
 */

class AbandonedCartRecovery {
  constructor() {
    // Configuration
    this.config = {
      cooldownMinutes: 60, // Don't show again for 1 hour after dismissal
      inactivitySeconds: 45, // Trigger after 45s of no interaction
      currencySymbol: window.Shopify?.currency?.active || '$',
      skipPaths: ['/cart', '/checkout', '/orders'] // Where NOT to show
    };

    // State
    this.state = {
      cart: { item_count: 0, items: [], total_price: 0 },
      hasTriggeredThisSession: false,
      isShowing: false,
      inactivityTimer: null,
      lastScrollY: window.scrollY
    };

    // DOM Elements
    this.modal = document.getElementById('AcrModal');
    if (!this.modal) return;
    
    this.elements = {
      image: document.getElementById('AcrProductImage'),
      count: document.getElementById('AcrItemCount'),
      subtotal: document.getElementById('AcrSubtotal'),
      closeBtns: document.querySelectorAll('[data-acr-close]'),
      cta: document.getElementById('AcrCheckoutBtn')
    };

    this.init();
  }

  async init() {
    // 1. Check if we should run on this page
    if (this.config.skipPaths.some(path => window.location.pathname.includes(path))) return;

    // 2. Check Cooldowns
    if (this.isOnCooldown()) return;

    // 3. Fetch Initial Cart State
    await this.fetchCart();

    // 4. If cart is empty, do not attach listeners yet
    if (this.state.cart.item_count === 0) {
      this.listenForCartUpdates(); // Wait for user to add something
      return;
    }

    // 5. Attach Abandonment Listeners
    this.attachEventListeners();
    this.startInactivityTimer();
    this.listenForCartUpdates();
  }

  /* --- API & Cart Sync --- */

  async fetchCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cartData = await response.json();
      this.state.cart = cartData;
      this.updateUI();
    } catch (error) {
      console.error('ACR: Failed to fetch cart', error);
    }
  }

  listenForCartUpdates() {
    // Intercept Fetch API to detect AJAX cart additions globally
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = typeof args[0] === 'string' ? args[0] : args[0].url;
      
      if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear')) {
        // Clone response because body can only be read once
        response.clone().json().then(data => {
          // If the endpoint returns the cart, update state immediately
          if(data.item_count !== undefined) {
             this.state.cart = data;
             this.updateUI();
             if(data.item_count > 0 && !this.state.hasTriggeredThisSession) {
               this.attachEventListeners();
             }
          } else {
             // Otherwise fetch latest cart state
             this.fetchCart().then(() => {
               if(this.state.cart.item_count > 0 && !this.state.hasTriggeredThisSession) {
                 this.attachEventListeners();
               }
             });
          }
        }).catch(() => {});
      }
      return response;
    };
  }

  updateUI() {
    if (this.state.cart.item_count === 0) return;

    const { items, total_price, item_count } = this.state.cart;
    const latestItem = items[0]; // Show the most recently added/first item

    // Update Image
    if (latestItem.image) {
      this.elements.image.src = this.getSizedImageUrl(latestItem.image, '200x200');
    }

    // Update Text
    this.elements.count.textContent = `${item_count} item${item_count > 1 ? 's' : ''}`;
    this.elements.subtotal.textContent = this.formatMoney(total_price);
  }

  /* --- Detection Logic --- */

  attachEventListeners() {
    if (this.listenersAttached) return;

    // Desktop: Exit Intent (Mouse leaving top viewport)
    document.addEventListener('mouseleave', this.handleDesktopExit.bind(this));

    // Mobile/Desktop: Tab Switch or Backgrounding
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Mobile: Fast Upward Scroll (Proxy for hitting URL bar / Back button)
    document.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

    // Inactivity resetters
    ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(evt => {
      document.addEventListener(evt, this.resetInactivityTimer.bind(this), { passive: true });
    });

    // UI Bindings
    this.elements.closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Analytics Hook
    this.elements.cta.addEventListener('click', () => {
      this.triggerAnalyticsEvent('recovery_cta_clicked');
    });

    this.listenersAttached = true;
  }

  handleDesktopExit(e) {
    if (e.clientY < 10) { // Mouse crossed top boundary
      this.trigger('desktop_exit_intent');
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      // User switched tabs or backgrounded the app. 
      // Trigger so it's waiting for them when they return.
      this.trigger('tab_abandonment');
    }
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const scrollSpeed = this.state.lastScrollY - currentScrollY;
    
    // If scrolling UP very fast on mobile, they might be reaching for the URL bar
    if (scrollSpeed > 100 && window.innerWidth <= 768) {
      this.trigger('mobile_fast_scroll_up');
    }
    
    this.state.lastScrollY = currentScrollY;
  }

  startInactivityTimer() {
    clearTimeout(this.state.inactivityTimer);
    this.state.inactivityTimer = setTimeout(() => {
      this.trigger('inactivity_timeout');
    }, this.config.inactivitySeconds * 1000);
  }

  resetInactivityTimer() {
    if (this.state.isShowing) return;
    this.startInactivityTimer();
  }

  /* --- Presentation Logic --- */

  trigger(reason) {
    // Validation Checks
    if (this.state.isShowing) return;
    if (this.state.hasTriggeredThisSession) return;
    if (this.isOnCooldown()) return;
    if (this.state.cart.item_count === 0) return;

    this.show();
    this.state.hasTriggeredThisSession = true;
    
    console.log(`ACR Triggered: ${reason}`);
    this.triggerAnalyticsEvent('recovery_popup_shown', { reason });
  }

  show() {
    this.state.isShowing = true;
    this.modal.setAttribute('aria-hidden', 'false');
    
    // Accessibility: Focus trap
    const focusableElements = this.modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if(focusableElements.length) {
       setTimeout(() => focusableElements[0].focus(), 100);
    }
    
    // Accessibility: ESC to close
    this.escListener = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.escListener);
  }

  close() {
    this.state.isShowing = false;
    this.modal.setAttribute('aria-hidden', 'true');
    this.setCooldown();
    document.removeEventListener('keydown', this.escListener);
    this.triggerAnalyticsEvent('recovery_popup_dismissed');
  }

  /* --- Suppression & Utils --- */

  isOnCooldown() {
    const cooldownUntil = localStorage.getItem('acr_cooldown');
    if (!cooldownUntil) return false;
    return new Date().getTime() < parseInt(cooldownUntil, 10);
  }

  setCooldown() {
    const expiry = new Date().getTime() + (this.config.cooldownMinutes * 60 * 1000);
    localStorage.setItem('acr_cooldown', expiry.toString());
  }

  formatMoney(cents) {
    // Uses basic formatting, can be expanded to hook into theme currency formatters
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    });
  }

  getSizedImageUrl(src, size) {
    if (!src) return '';
    return src.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
              .replace(/\.jpg|\.png|\.gif|\.jpeg/g, function (match) {
                return '_' + size + match;
              });
  }

  triggerAnalyticsEvent(eventName, payload = {}) {
    // Custom Event for GTM / Custom scripts
    const event = new CustomEvent(eventName, { detail: payload });
    window.dispatchEvent(event);

    // Meta Pixel Fallback
    if (typeof fbq === 'function' && eventName === 'recovery_popup_shown') {
      fbq('trackCustom', 'AbandonedCartRecoveryShown');
    }
    
    // GA4 Fallback
    if (typeof gtag === 'function' && eventName === 'recovery_popup_shown') {
      gtag('event', 'abandoned_cart_recovery_shown');
    }
  }
}

// Initialize on DOM Ready
document.addEventListener('DOMContentLoaded', () => {
  window.ACR = new AbandonedCartRecovery();
});